import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { CustomLayout } from '../Models/custom-layout.model';
import { CustomLayoutsService } from '../custom-layouts.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CustomLayoutField } from '../Models/custom-layout-field.model';
import { CustomLayoutSection } from '../Models/custom-layout-section.model';
import { CustomLayoutType } from '../Models/custom-layout-type.model';
import { UserService } from '../../../shared-components/user/user.service';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Subscription } from 'rxjs';
import { SharedService } from '../../../shared-components/shared.service';
import { System } from '../../../shared-components/System';
import { LayoutUnsavedChangesDialogComponent } from './layout-unsaved-changes-dialog/layout-unsaved-changes-dialog.component';
import { LoginService } from '../../../shared-modules/login/login.service';
import { Router } from '@angular/router';
import { SelectedSite } from '../../controls/selected-sites-dialog/selected-site';
import { SelectedSitesDialogComponent } from '../../controls/selected-sites-dialog/selected-sites-dialog.component';
import { ErrorList } from '../../controls/error-prompt/error-list.model';
import { YearService } from '../../year/year.service';

@AutoUnsubscribe({ includeArrays: true })
@Component({
    selector: 'app-layout-editor',
    templateUrl: './layout-editor.component.html',
    styleUrls: ['./layout-editor.component.css'],
})
export class LayoutEditorComponent implements AfterViewInit, OnDestroy {

    operation: string;
    layout: CustomLayout = new CustomLayout();
    layoutType: CustomLayoutType;
    previousType: CustomLayoutType;
    selectedFields: CustomLayoutField[] = [];
    colBreakFieldId: number;
    userId: number;
    subscriptions: Subscription[] = [];
    unsavedChanges = false;
    unsavedFieldChanges = false;
    dqFieldsRetrieved = false;
    v10: boolean;
    layoutTypes: CustomLayoutType[] = [];
    isAcademicHistory: boolean;
    errors: ErrorList[] = [];
    headerLabel: string = '';
    duplicate: boolean = false;

    @ViewChild('rbgScopeRef', { read: ElementRef }) rbgScopeRef: ElementRef;
    @ViewChild('txtNameRef', { read: ElementRef, static: true }) txtNameRef: ElementRef;

    constructor(
        private customLayoutService: CustomLayoutsService,
        private dialogRef: MatDialogRef<LayoutEditorComponent>,
        private dialog: MatDialog,
        private userService: UserService,
        private yearService: YearService,
        private sharedService: SharedService,
        private loginService: LoginService,
        private router: Router
    ) { }

    ngAfterViewInit() {
        this.v10 = this.loginService.LoginSettings.V10;
        this.isAcademicHistory = this.router.url.indexOf('academicHistory') > -1 || this.router.url.indexOf('registrationEnrollment') > -1;
        this.userId = this.userService.currentUser.Id;
        if (this.isAcademicHistory) {
            this.layoutTypes = this.customLayoutService.layoutTypes.filter(t => t.Name.indexOf('Academic History') > -1);
        }
        else {
            this.layoutTypes = this.customLayoutService.selectedLayoutType.Name != 'Special Services' ? this.customLayoutService.layoutTypes.filter(t => t.Name.indexOf('Academic History') == -1 && t.Name.indexOf('Special Services')) : this.customLayoutService.layoutTypes.filter(t => t.Name.indexOf('Academic History') == -1 && t.Name.indexOf('Behavior Quick Add') == -1);
        }

        this.layoutType = (this.operation == 'Add') ? this.layoutTypes.filter(t => t.Id == this.customLayoutService.selectedLayoutType.Id)[0] : this.layoutTypes.filter(t => t.Id == this.layout.LayoutTypeID)[0];
        if (this.operation == 'Add' && this.customLayoutService.selectedLayoutType.Name != 'Special Services') {
            this.headerLabel = (this.isAcademicHistory) ? 'Add New Academic History Layout' : 'Add New Behavior Quick Add Layout';
        } else if (this.operation == 'Add' && this.customLayoutService.selectedLayoutType.Name == 'Special Services') {
            this.headerLabel = 'Add New Special Services Layout';
        }
        else {
            this.headerLabel = 'Edit ' + this.layout.Name;
        }
        this.previousType = this.layoutType;
        this.setLayout(this.layout);
        if (this.customLayoutService.specialFields.length == 0) {
            this.subscriptions.push(this.customLayoutService.getSpecialFields().subscribe(fields => {
                this.customLayoutService.specialFields = fields;
                const colBreak = fields.find(f => f.ColumnBreak == true);
                if (colBreak) {
                    this.colBreakFieldId = colBreak.Id;
                }
            }));
        }
        else {
            const colBreak = this.customLayoutService.specialFields.find(f => f.ColumnBreak == true);
            if (colBreak) {
                this.colBreakFieldId = colBreak.Id;
            }
        }
        if (this.customLayoutService.dataQueryFields.length == 0) {
            this.subscriptions.push(this.customLayoutService.getDataQueryFields((this.layoutType.Name.indexOf('Behavior Quick Add') > -1 ? 'Behavior' : '')).subscribe(fields => {
                this.customLayoutService.dataQueryFields = fields;
                this.dqFieldsRetrieved = true;
            }));
        }
    }

    setLayout(layout: CustomLayout) {
        if (this.operation == 'Edit' && layout && !layout.TylerDefault)
            this.layout = System.copyObject<CustomLayout>(layout, new CustomLayout());
        else if (!this.duplicate)
            this.setFieldsToDefault();

        this.layout.Sections.forEach(section => {
            this.checkRequiredFields(section);
            this.filterAvailableFields(section);
            this.reorderAvailableFields(section);
            this.checkColumnCounts(section);
        });
    }

    layoutTypeChanged() {
        if (this.unsavedFieldChanges) {
            const dRef = this.dialog.open(LayoutUnsavedChangesDialogComponent, {
                disableClose: true,
                data: {
                    enableSave: false,
                    confirmDialog: true,
                    dialogTitle: 'Changing the layout type will reset all field selections to their defaults. Do you want to continue?'
                }
            });
            this.subscriptions.push(dRef.afterClosed().subscribe(result => {
                switch (result) {
                    case 'continueWithoutSave': {
                        this.layout.LayoutTypeID = this.layoutType.Id;
                        const defaultLayout = this.customLayoutService.savedLayouts.find(o => o.LayoutTypeID == this.layoutType.Id && o.TylerDefault);
                        this.layout.Sections = System.copyObject<CustomLayout>(defaultLayout, new CustomLayout()).Sections;
                        this.previousType = this.layoutType;
                        break;
                    }
                    case 'cancel': {
                        this.layoutType = this.previousType;
                        break;
                    }
                    default: {
                        this.layoutType = this.previousType;
                        break;
                    }
                }
            }));
        }
        else {
            this.layout.LayoutTypeID = this.layoutType.Id;
            const defaultLayout = this.customLayoutService.savedLayouts.find(o => o.LayoutTypeID == this.layoutType.Id && o.TylerDefault);
            this.layout.Sections = System.copyObject<CustomLayout>(defaultLayout, new CustomLayout()).Sections;
            this.previousType = this.layoutType;
        }
    }


    getFieldDisplayName(field: CustomLayoutField) {
        if (!this.v10) {
            let fieldName = '';
            if (field.DQFieldID > 0) {
                if (this.loginService.LoginSettings.V10) {
                    const dqField = this.customLayoutService.dataQueryFields.find(f => f.ID == field.DQFieldID);
                    if (dqField) {
                        fieldName = dqField.DisplayName;
                    }
                }
                else {
                    const dqField = this.customLayoutService.dataQueryFields.find(f => f.ID == field.DQFieldID && f.CategoryID == field.CategoryId);
                    if (dqField) {
                        fieldName = dqField.DisplayName;
                    }
                }
            }
            else if (field.SpecialFieldID > 0) {
                const sField = this.customLayoutService.specialFields.find(f => f.Id == field.SpecialFieldID);
                if (sField) {
                    fieldName = sField.DisplayName;
                }
            }
            return (fieldName == 'Year End Date' || fieldName == 'Year Start Date') ? false : true;
        } else {
            return true;
        }
    }

    checkRequiredFields(section: CustomLayoutSection) {
        section.AvailableFields.forEach(field => {
            if (field.RequiredForDisplay && section.SelectedFields.find(o => o.Id == field.Id) == null) {
                section.SelectedFields.push(field);
            }
        });
    }

    filterAvailableFields(section: CustomLayoutSection) {
        section.SelectedFields.forEach(field => {
            let index = -1;
            if (field.DQFieldID > 0) {
                index = section.AvailableFields.findIndex(f => f.DQFieldID == field.DQFieldID);
            }
            else if (field.SpecialFieldID > 0) {
                index = section.AvailableFields.findIndex(f => f.SpecialFieldID == field.SpecialFieldID);
            }
            if (index >= 0) {
                section.AvailableFields.splice(index, 1);
            }
        });
    }

    addField(section: CustomLayoutSection, index: number) {
        this.unsavedFieldChanges = true;
        section.SelectedFields.push(this.selectedFields[index]);
        let i = -1;
        if (this.selectedFields[index].DQFieldID > 0) {
            i = section.AvailableFields.findIndex(f => f.DQFieldID == this.selectedFields[index].DQFieldID);
        }
        else if (this.selectedFields[index].SpecialFieldID > 0) {
            i = section.AvailableFields.findIndex(f => f.SpecialFieldID == this.selectedFields[index].SpecialFieldID);
        }
        if (i >= 0) {
            section.AvailableFields.splice(i, 1);
        }
        this.selectedFields[index] = null;
        this.setFieldOrder(section);
    }

    drop(event: CdkDragDrop<string[]>, section: CustomLayoutSection) {
        this.unsavedFieldChanges = true;
        moveItemInArray(section.SelectedFields, event.previousIndex, event.currentIndex);
        this.setFieldOrder(section);
    }

    setFieldOrder(section: CustomLayoutSection) {
        for (let i = 0; i < section.SelectedFields.length; i++) {
            section.SelectedFields[i].Order = i + 1;
        }
    }

    removeItemClick(section: CustomLayoutSection, index: number) {
        this.unsavedFieldChanges = true;
        const field = section.SelectedFields[index];
        section.AvailableFields.push(field);
        section.SelectedFields.splice(index, 1);
        this.reorderAvailableFields(section);
        this.setFieldOrder(section);
    }

    reorderAvailableFields(section: CustomLayoutSection) {
        section.AvailableFields.sort((a: CustomLayoutField, b: CustomLayoutField): number => {
            let aName = '';
            let bName = '';
            let comparison = 0;

            if (a.DQFieldID > 0) {
                const aField = this.customLayoutService.dataQueryFields.find(f => f.ID == a.DQFieldID);
                if (aField) {
                    aName = aField.DisplayName;
                }
            }
            else if (a.SpecialFieldID > 0) {
                const aField = this.customLayoutService.specialFields.find(f => f.Id == a.SpecialFieldID);
                if (aField) {
                    aName = aField.DisplayName;
                }
            }

            if (b.DQFieldID > 0) {
                const bField = this.customLayoutService.dataQueryFields.find(f => f.ID == b.DQFieldID);
                if (bField) {
                    bName = bField.DisplayName;
                }
            }
            else if (b.SpecialFieldID > 0) {
                const bField = this.customLayoutService.specialFields.find(f => f.Id == b.SpecialFieldID);
                if (bField) {
                    bName = bField.DisplayName;
                }
            }

            if (aName > bName) {
                comparison = 1;
            }
            else if (aName < bName) {
                comparison = -1;
            }

            return comparison;
        });
    }

    checkColumnCounts(section: CustomLayoutSection) {
        const requiredColumnCount = section.Columns;
        const columnField = this.customLayoutService.specialFields.find(f => f.ColumnBreak == true);
        if (columnField) {
            let cf = section.AvailableFields.find(o => o.SpecialFieldID == columnField.Id);
            if (cf == null) {
                cf = section.SelectedFields.find(o => o.SpecialFieldID == columnField.Id);
            }
            if (cf == null) {
                cf = new CustomLayoutField();
                cf.SectionID = section.Id;
                cf.SpecialFieldID = columnField.Id;
                cf.AllowEdit = false;
            }

            const actualColumnnCount = section.SelectedFields.filter(f => f.SpecialFieldID == columnField.Id).length + 1;
            if (requiredColumnCount > actualColumnnCount) {
                for (let i = 0; i < requiredColumnCount - actualColumnnCount; i++) {
                    section.SelectedFields.push(cf);
                }
            }
            else if (requiredColumnCount < actualColumnnCount) {
                let colsToRemove = actualColumnnCount - requiredColumnCount;
                for (let i = section.SelectedFields.length - 1; i >= 0; i--) {
                    if (colsToRemove > 0) {
                        if (section.SelectedFields[i].SpecialFieldID == columnField.Id) {
                            section.SelectedFields.splice(i, 1);
                            colsToRemove--;
                        }
                    }
                    else {
                        break;
                    }
                }
            }
        }
        // const colBreakIndex = section.AvailableFields.findIndex(o => o.SpecialFieldID == columnField.Id);
        // if (colBreakIndex >= 0) {
        //     section.AvailableFields.splice(colBreakIndex, 1);
        // }
        this.setFieldOrder(section);
    }

    cancelClick() {
        this.dialogRef.close();
    }

    revertChangesClick() {
        this.unsavedFieldChanges = false;
        this.setLayout(this.customLayoutService.selectedLayout);
    }

    setFieldsToDefault() {
        const defaultLayout = this.customLayoutService.savedLayouts.find(o => o.LayoutTypeID == this.layoutType.Id && o.TylerDefault);
        this.layout = System.copyObject<CustomLayout>(defaultLayout, new CustomLayout());
        this.layout.Name = '';
        this.layout.Description = '';
        this.layout.Id = null;
        this.layout.TylerDefault = false;
        this.layout.Owner = this.userService.currentUser.Id;
    }

    saveClick(close: boolean) {
        if (this.validate()) {
            this.customLayoutService.saveLayout(this.layout).subscribe(result => {
                if (result && result.Id > 0) {
                    this.sharedService.showGoodSnackbar();
                    this.unsavedChanges = false;

                    if (close) {
                        this.customLayoutService.getLayoutsByType(this.customLayoutService.selectedLayoutType.Id, this.yearService.defaultYear.Id, this.customLayoutService.selectedLayoutType.Name != 'Special Services' ? true : false).subscribe((layouts) => {
                            if (this.customLayoutService.selectedLayoutType.Id != this.layoutType.Id)
                                this.customLayoutService.selectedLayoutType = this.customLayoutService.layoutTypes.filter(t => t.Id == this.layoutType.Id)[0];
                            this.customLayoutService.savedLayouts = layouts;
                            this.customLayoutService.selectedLayout = result;
                            this.dialogRef.close();
                        });
                    }
                    else
                        this.sharedService.showBadSnackbar();
                }
            });
        }
    }

    validate(): boolean {
        this.errors = [];
        let existingLayout = this.customLayoutService.savedLayouts.filter(f => f.Name == this.layout.Name && f.Id != this.layout.Id)[0];
        if (this.layout.Scope == 'Selected Sites' && !this.layout.SelectedSites) {
            this.errors.push({
                fieldRef: this.rbgScopeRef,
                fieldName: 'Scope',
                errorMsg: 'No Sites Selected.'
            });
        }
        if (!this.layout.Name) {
            this.errors.push({
                fieldRef: this.txtNameRef,
                fieldName: 'Name',
                errorMsg: 'Required field.'
            });
        }
        else if (existingLayout) {
            this.errors.push({
                fieldRef: this.txtNameRef,
                fieldName: 'Name',
                errorMsg: 'A layout with that name already exists.'
            });
        }
        return this.errors.length == 0;
    }

    layoutNameChanged() {
        this.unsavedChanges = true;
    }

    selectedSitesClick() {
        let selectedSitesString = this.layout.SelectedSites;
        if (!selectedSitesString) {
            selectedSitesString = '';
        }
        const selectedSites: SelectedSite[] = [];
        selectedSitesString.split(',').forEach(siteIdString => {
            const siteId = Number(siteIdString.trim());
            if (!isNaN(siteId)) {
                const ss = new SelectedSite();
                ss.Id = siteId;
                selectedSites.push(ss);
            }
        });
        const selectedSitesDialogBox = this.dialog.open(SelectedSitesDialogComponent, {
            disableClose: true,
            width: '700px',
            data: {
                selectedSites: selectedSites
            }
        });
        selectedSitesDialogBox.afterClosed().subscribe((results: SelectedSite[]) => {
            if (results) {
                this.layout.SelectedSites = results.map(o => o.Id).join(', ');
            }
        });
    }

    isSpecialFieldDraggable(field: CustomLayoutField): boolean {
        const specialField = this.customLayoutService.specialFields.find(o => o.Id == field.SpecialFieldID);
        return (!specialField || specialField.DefinesColumns || specialField.IsSection);
    }

    hideSectionChange(section: CustomLayoutSection) {
        this.hideChildSections(section, section.Hidden);
    }

    hideChildSections(section: CustomLayoutSection, hidden: boolean) {
        this.layout.Sections.forEach(s => {
            if (s.ParentSectionID == section.Id) {
                s.Hidden = hidden;
                s.ParentSectionIsHidden = hidden;
                this.hideChildSections(s, hidden);
            }
        });
    }

    hideDisableCheck(section: string): boolean {
        let disableHide: string[] = ['Event/Incident', ''];
        return disableHide.findIndex(x => x == section) != -1;
    }

    ngOnDestroy(): void {
    }
}